import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { FrameworkContractOut } from './rest-api/rest-api';

export type EfektikaRequirementKey = keyof EfektikaRequirements;

export enum EfektikaContractsOpfFeeLimitation {
  NONE, // no limitation, two contracts and both contracts are opfFee = true
  SINGLE_DIP, // one single contract with opfFee = true or two contract where one true and second false, cannot change dip
  CANNOT_CREATE, // only opfFee = false contracts (one single or two both), cannot create efektika
}

export enum EfektikaInvestmentType {
  REGULAR = 'REGULAR', INITIAL = 'INITIAL'
}

export interface EfektikaRequirements {
  isModelStrategyEfektika: boolean;
  isFeeConfigEfektika: boolean;
  feeConfigsAllowingEfektika: string[];
  contractsOpfFeeLimitation: EfektikaContractsOpfFeeLimitation;
  dipLimitContract: FrameworkContractOut | null;
  newClient: boolean;
}

export interface EfektikaCardSettings {
  enabled: boolean,
  tooltipKey: string,
  efektikaConfigNames: string | null
}

@Injectable({
  providedIn: 'root'
})
export class EfektikaConsultService {
  private consultCreatingEfektikaConfirmed = new Subject<void>();
  public consultCreatingEfektikaConfirmed$ = this.consultCreatingEfektikaConfirmed.asObservable();

  private efektikaOptions: EfektikaRequirements = {
    isModelStrategyEfektika: false,
    isFeeConfigEfektika: false,
    feeConfigsAllowingEfektika: [],
    contractsOpfFeeLimitation: EfektikaContractsOpfFeeLimitation.CANNOT_CREATE,
    dipLimitContract: null,
    newClient: false,
  }

  public getEfektikaOptions(): EfektikaRequirements {
    return this.efektikaOptions;
  }

  public clearDataAndSetDefault(): void {
    this.efektikaOptions =  {
      isModelStrategyEfektika: false,
      isFeeConfigEfektika: false,
      feeConfigsAllowingEfektika: [],
      contractsOpfFeeLimitation: EfektikaContractsOpfFeeLimitation.CANNOT_CREATE,
      dipLimitContract: null,
      newClient: false,
    };
  }

  public resetForTheSameClient(): void {
    const contractLimit = this.efektikaOptions.contractsOpfFeeLimitation;
    const dipLimitContract = this.efektikaOptions.dipLimitContract ? {...this.efektikaOptions.dipLimitContract} : null;
    const isFeeConfigEfektika = this.efektikaOptions.isFeeConfigEfektika;
    const feeConfigsAllowingEfektika = this.efektikaOptions.feeConfigsAllowingEfektika;
    this.efektikaOptions =  {
      isModelStrategyEfektika: false,
      newClient: false,
      isFeeConfigEfektika,
      feeConfigsAllowingEfektika,
      contractsOpfFeeLimitation: contractLimit,
      dipLimitContract: dipLimitContract,
    };
  }

  public triggerConfirmConsultCreatingEfektika(): void {
    this.consultCreatingEfektikaConfirmed.next();
  }

  public updateEfektikaRequirements<
    K extends EfektikaRequirementKey
  >(
    key: K,
    value: EfektikaRequirements[K]
  ): void {
    this.efektikaOptions[key] = value
  }

  private getFCOpfFeeLimitation(contracts: FrameworkContractOut[]): EfektikaContractsOpfFeeLimitation {
    if (contracts.every(c => c.opfFeeImplementation) && contracts.length === 2)
      return EfektikaContractsOpfFeeLimitation.NONE;
    else if ((contracts.length === 1 && contracts[0].opfFeeImplementation) || contracts.some(c => c.opfFeeImplementation)) 
      return EfektikaContractsOpfFeeLimitation.SINGLE_DIP;
    else
      return EfektikaContractsOpfFeeLimitation.CANNOT_CREATE;
  }

  public setEfektikaFCsOpfFeeLimitation(contracts: FrameworkContractOut[]): void {
    this.updateEfektikaRequirements('newClient', false);
    const limitation = this.getFCOpfFeeLimitation(contracts);
    this.updateEfektikaRequirements('contractsOpfFeeLimitation', limitation);
    if (limitation === EfektikaContractsOpfFeeLimitation.SINGLE_DIP) {
      this.updateEfektikaRequirements('dipLimitContract', contracts.find(c => c.opfFeeImplementation) ?? null);
    }
  }

  public get canCreateEfektika(): boolean {
    return this.getEfektikaOptions().feeConfigsAllowingEfektika.length > 0 &&
      this.getEfektikaOptions().contractsOpfFeeLimitation !== EfektikaContractsOpfFeeLimitation.CANNOT_CREATE ||
      this.getEfektikaOptions().newClient;
  }
}
